/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useEffect} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Alert,
    Box,
    Button,
    Image,
    Link,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AlertIcon, BrandLogo} from '@salesforce/retail-react-app/app/components/icons'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import ResetPasswordFields from '@salesforce/retail-react-app/app/components/forms/reset-password-fields'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {headingText, linkStyle, subHeadingText} from './index.style'
import {ERROR_MESSAGE_TIMEOUT} from '../../constants'

const ResetPasswordForm = ({submitForm, clickSignIn = noop, form, onResendLink = noop}) => {
    const globalError = form.formState.errors?.global
    const globalErrorMessage = globalError?.message

    useEffect(() => {
        if (!globalErrorMessage) {
            return undefined
        }
        const timeout = setTimeout(() => form.clearErrors('global'), ERROR_MESSAGE_TIMEOUT)
        return () => clearTimeout(timeout)
    }, [globalErrorMessage, form])

    useEffect(() => {
        const storedEmail =
            typeof window !== 'undefined' ? localStorage.getItem('passwordResetEmail') || '' : ''

        if (storedEmail) {
            form.setValue('email', storedEmail)
        }
    }, [form])

    return (
        <Fragment>
            {!form.formState.isSubmitSuccessful ? (
                <>
                    <Stack justify="center" align="center" spacing={{base: '15px', md: '20px'}}>
                        <Image
                            src={getAssetUrl('static/img/account/my-cavenders.svg')}
                            alt="My Cavenders Logo"
                            width={{base: '205px', md: '265px'}}
                            height={{base: '41px', md: '53px'}}
                        />
                        <Text align="center" sx={headingText}>
                            <FormattedMessage
                                defaultMessage="Reset Password"
                                id="reset_password_form.title.reset_password"
                            />
                        </Text>
                        <Text align="center" sx={subHeadingText}>
                            <FormattedMessage
                                defaultMessage="Enter your email to receive instructions on how to reset your password"
                                id="reset_password_form.message.enter_your_email"
                            />
                        </Text>
                    </Stack>
                    <form onSubmit={form.handleSubmit(submitForm)} data-testid="sf-auth-modal-form">
                        <Stack spacing={{base: '20px', md: '25px'}}>
                            {globalError && (
                                <Alert
                                    status="error"
                                    sx={{mt: '25px', mb: {base: '-20px', md: '-25px'}}}
                                >
                                    <AlertIcon color="red.500" boxSize={4} />
                                    <Text fontSize="sm" ml={3} mb={0} textAlign="center">
                                        {globalError.message}
                                    </Text>
                                </Alert>
                            )}
                            <Box sx={{mt: {md: '5px'}}}>
                                <ResetPasswordFields form={form} />
                            </Box>
                            <Button
                                type="submit"
                                onClick={() => form.clearErrors('global')}
                                isLoading={form.formState.isSubmitting}
                                sx={{
                                    backgroundColor: 'cav_red',
                                    _hover: {
                                        backgroundColor: 'white',
                                        color: 'cav_red'
                                    }
                                }}
                            >
                                <FormattedMessage
                                    defaultMessage="Send Verification Link"
                                    id="reset_password_form.verification_text"
                                />
                            </Button>
                        </Stack>
                    </form>
                </>
            ) : (
                <Stack justify="center" align="center" spacing={{base: '15px', md: '20px'}}>
                    <Image
                        src={getAssetUrl('static/img/account/my-cavenders.svg')}
                        alt="My Cavenders Logo"
                        width={{base: '205px', md: '265px'}}
                        height={{base: '41px', md: '53px'}}
                    />
                    <Text align="center" sx={headingText}>
                        <FormattedMessage
                            defaultMessage="Reset Password Request"
                            id="auth_modal.password_reset_success.title.reset_password_request"
                        />
                    </Text>
                    <Stack spacing={{base: -1, md: '4px'}}>
                        <Text align="center" sx={{...subHeadingText, m: 0}}>
                            <FormattedMessage
                                defaultMessage="We’ve emailed your password reset link. It may take a few minutes—check your junk folder if needed."
                                id="auth_modal.password_reset_success.info.will_email"
                            />
                        </Text>
                    </Stack>
                </Stack>
            )}
        </Fragment>
    )
}

ResetPasswordForm.propTypes = {
    submitForm: PropTypes.func,
    clickSignIn: PropTypes.func,
    form: PropTypes.object,
    onResendLink: PropTypes.func
}

export default ResetPasswordForm
